import { HTTPError } from 'ky';

// The BE's structured `{ error: { code, message, retryable } }` envelope: its typed error, the
// reader and the rethrow helper. Used by the eval page's reads (pages/packages/show/eval3-new) and
// the case page's coverage map, so neither page imports the other's internals.

/**
 * A failure carrying the BE's structured `{ error: { code, message, retryable } }` envelope
 * (`Queries::Packages::StructuredErrorRendering`).
 *
 * The base class for every endpoint that speaks that envelope; an endpoint that needs its own
 * error name subclasses it with its own `errorName`. Consumers gate on
 * `instanceof ApiEnvelopeError && !retryable`.
 *
 * `retryable` defaults to TRUE: an unparseable body keeps the retry affordance rather than
 * removing it on a guess. Removing a working Retry is worse than offering a doomed one.
 *
 * The name is carried by a static rather than by `new.target.name`, deliberately. Class
 * declaration names are MINIFIED in the production bundle (verified: the current bundle
 * contains exactly one occurrence of each of these names — the hardcoded string literal, not
 * the class), so `new.target.name` would ship a mangled one-or-two-character name and silently
 * wreck error grouping in Rollbar/Datadog. A static string literal survives minification.
 */
export class ApiEnvelopeError extends Error {
  /** Overridden by each subclass; minification-safe because it is a string literal. */
  static errorName = 'ApiEnvelopeError';

  code?: string;
  retryable: boolean;

  /**
   * Builds a typed envelope failure.
   *
   * @param {string} message - Human-readable failure message (BE envelope message, or ky's).
   * @param {string | undefined} code - BE error code (e.g. 'calculation_data_unavailable'), if the envelope carried one.
   * @param {boolean} retryable - Whether a retry could plausibly succeed; defaults to true when unknown.
   */
  constructor(message: string, code?: string, retryable = true) {
    super(message);
    this.name = (new.target as typeof ApiEnvelopeError).errorName;
    this.code = code;
    this.retryable = retryable;
  }
}

/** The BE's structured error envelope (`Queries::Packages::StructuredErrorRendering`). */
export interface ApiErrorEnvelope {
  code?: string;
  message?: string;
  field?: string;
  retryable?: boolean;
}

/**
 * Reads the structured `{ error: { code, message, … } }` envelope off a ky HTTPError body: the one
 * parser of the envelope, used by throwTypedEnvelopeError.
 *
 * @param {unknown} err - The caught error; anything that isn't an HTTPError yields null.
 * @return {Promise<ApiErrorEnvelope | null>} The envelope, or null when the body isn't the structured shape.
 */
export async function readErrorEnvelope(
  err: unknown
): Promise<ApiErrorEnvelope | null> {
  if (!(err instanceof HTTPError)) {
    return null;
  }
  const body = (await err.response.json().catch(() => null)) as {
    error?: ApiErrorEnvelope;
  } | null;
  return body?.error ?? null;
}

/**
 * Rethrows a caught fetch failure as a typed envelope error, or rethrows it untouched.
 *
 * The one place the envelope-to-error conversion happens, so every endpoint that speaks the
 * envelope agrees on it. Two properties are load-bearing and easy to break by hand:
 *
 *  - Anything that is NOT an `HTTPError` is rethrown AS-IS. That is what keeps a cancelled
 *    request a cancellation: React Query aborts in-flight fetches on unmount and on a key
 *    change under `keepPreviousData`, and ky surfaces that as an `AbortError`, not an
 *    `HTTPError`. Typing it would turn every navigation away into a user-facing error banner.
 *  - `retryable` falls back to TRUE, so an unparseable body keeps the Retry affordance.
 *
 * @param {unknown} err - The caught error.
 * @param {Function} ErrorClass - The ApiEnvelopeError subclass to construct.
 * @param {string} [fallbackMessage] - The message when the body names none (else ky's own).
 * @return {Promise<never>} Always throws.
 */
export async function throwTypedEnvelopeError(
  err: unknown,
  ErrorClass: new (
    message: string,
    code?: string,
    retryable?: boolean
  ) => ApiEnvelopeError = ApiEnvelopeError,
  fallbackMessage?: string
): Promise<never> {
  if (!(err instanceof HTTPError)) {
    throw err;
  }
  const envelope = await readErrorEnvelope(err);
  throw new ErrorClass(
    envelope?.message || fallbackMessage || err.message,
    envelope?.code,
    envelope?.retryable ?? true
  );
}
